import { useState, useEffect } from "react";
import { useDrive } from "../App";
import { Sidebar } from "./Dashboard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Link2, Receipt, FileText, CheckCircle2, ArrowRight, Zap } from "lucide-react";

export default function Matching() {
  const { initialized, folderIds, listFiles, getFileAsJson, uploadFile } = useDrive();
  const [unmatchedReceipts, setUnmatchedReceipts] = useState([]);
  const [unmatchedTransactions, setUnmatchedTransactions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    if (initialized && folderIds.receipts && folderIds.bank_statements) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load receipts
      const allReceipts = [];
      const monthFolders = await listFiles(folderIds.receipts, 'application/vnd.google-apps.folder');
      
      for (const folder of monthFolders) {
        const files = await listFiles(folder.id);
        const jsonFiles = files.filter(f => f.name.endsWith('.json'));
        
        for (const jsonFile of jsonFiles) {
          try {
            const receipt = await getFileAsJson(jsonFile.id);
            allReceipts.push({ ...receipt, fileId: jsonFile.id, monthFolder: folder.name, monthFolderId: folder.id });
          } catch (e) {}
        }
      }
      
      // Load transactions
      const allTransactions = [];
      const statementFiles = await listFiles(folderIds.bank_statements);
      
      for (const file of statementFiles) {
        if (file.name.endsWith('.json')) {
          try {
            const statement = await getFileAsJson(file.id);
            if (statement.transactions) {
              statement.transactions.forEach(t => {
                allTransactions.push({ ...t, statementFileId: file.id, period: statement.period });
              });
            }
          } catch (e) {}
        }
      }
      
      // Filter unmatched
      const unmatched_r = allReceipts.filter(r => !r.isMatched);
      const unmatched_t = allTransactions.filter(t => !t.isMatched);
      
      setUnmatchedReceipts(unmatched_r);
      setUnmatchedTransactions(unmatched_t);
      
      // Generate suggestions
      const sugg = [];
      for (const receipt of unmatched_r) {
        const receiptDate = new Date(receipt.transactionDate);
        const receiptAmount = Math.abs(receipt.totalAmount);
        
        for (const txn of unmatched_t) {
          const txnDate = new Date(txn.date);
          const txnAmount = Math.abs(txn.amount);
          
          const dateDiff = Math.abs((receiptDate - txnDate) / (1000 * 60 * 60 * 24));
          if (dateDiff > 3) continue;
          
          if (receiptAmount > 0) {
            const amountDiff = Math.abs(receiptAmount - txnAmount) / receiptAmount;
            if (amountDiff > 0.05) continue;
          }
          
          const confidence = 1.0 - (dateDiff * 0.1) - (receiptAmount > 0 ? Math.abs(receiptAmount - txnAmount) / receiptAmount * 0.5 : 0);
          
          sugg.push({ receipt, transaction: txn, confidence: Math.max(0, confidence) });
        }
      }
      
      sugg.sort((a, b) => b.confidence - a.confidence);
      setSuggestions(sugg.slice(0, 20));
      
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load matching data");
    } finally {
      setLoading(false);
    }
  };

  const handleMatch = async (receipt, transaction) => {
    try {
      // Update receipt
      const updatedReceipt = {
        ...receipt,
        isMatched: true,
        matchedTransactionIds: [...(receipt.matchedTransactionIds || []), transaction.id]
      };
      delete updatedReceipt.fileId;
      delete updatedReceipt.monthFolder;
      delete updatedReceipt.monthFolderId;
      
      await uploadFile(`${receipt.id}.json`, JSON.stringify(updatedReceipt, null, 2), 'application/json', null, receipt.fileId);
      
      // Update transaction in statement
      const statementFiles = await listFiles(folderIds.bank_statements);
      const statementFile = statementFiles.find(f => f.id === transaction.statementFileId);
      
      if (statementFile) {
        const statement = await getFileAsJson(statementFile.id);
        statement.transactions = statement.transactions.map(t => 
          t.id === transaction.id ? { ...t, isMatched: true, matchedReceiptId: receipt.id } : t
        );
        await uploadFile(statementFile.name, JSON.stringify(statement, null, 2), 'application/json', null, statementFile.id);
      }
      
      toast.success("Items matched successfully!");
      setSelectedReceipt(null);
      setSelectedTransaction(null);
      loadData();
    } catch (error) {
      console.error("Error matching:", error);
      toast.error("Failed to match items");
    }
  };

  if (!initialized || loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="matching" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="matching" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Matching Workspace
            </h1>
            <p className="text-slate-600 mt-1">Match receipts to bank transactions</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Unmatched Receipts</p>
                  <p className="text-2xl font-bold text-slate-900">{unmatchedReceipts.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Unmatched Transactions</p>
                  <p className="text-2xl font-bold text-slate-900">{unmatchedTransactions.length}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Suggested Matches</p>
                  <p className="text-2xl font-bold text-slate-900">{suggestions.length}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <Card className="mb-8 bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  Suggested Matches
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-y border-slate-200">
                        <th className="py-3 px-4 text-left font-medium">Receipt</th>
                        <th className="py-3 px-4 text-center font-medium"></th>
                        <th className="py-3 px-4 text-left font-medium">Transaction</th>
                        <th className="py-3 px-4 text-center font-medium">Confidence</th>
                        <th className="py-3 px-4 text-center font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.slice(0, 10).map((s, index) => (
                        <tr key={index} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-900">{s.receipt.merchantName}</p>
                              <p className="text-sm text-slate-500">{s.receipt.transactionDate?.slice(0, 10)} • £{s.receipt.totalAmount?.toFixed(2)}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <ArrowRight className="w-5 h-5 text-slate-400 mx-auto" />
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-slate-900">{s.transaction.description}</p>
                              <p className="text-sm text-slate-500">{s.transaction.date} • £{Math.abs(s.transaction.amount).toFixed(2)}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              s.confidence >= 0.8 ? "bg-emerald-50 text-emerald-700" : s.confidence >= 0.5 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
                            }`}>
                              {(s.confidence * 100).toFixed(0)}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button size="sm" onClick={() => handleMatch(s.receipt, s.transaction)}>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Manual Matching */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Unmatched Receipts
                </CardTitle>
              </CardHeader>
              <ScrollArea className="h-[400px]">
                <CardContent className="p-0">
                  {unmatchedReceipts.length === 0 ? (
                    <div className="p-8 text-center">
                      <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                      <p className="text-slate-500">All receipts are matched!</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {unmatchedReceipts.map((receipt, index) => (
                        <button
                          key={receipt.id}
                          onClick={() => setSelectedReceipt(selectedReceipt?.id === receipt.id ? null : receipt)}
                          className={`w-full p-4 text-left transition-colors ${
                            selectedReceipt?.id === receipt.id ? "bg-blue-50 border-l-4 border-l-blue-600" : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-slate-900">{receipt.merchantName}</p>
                              <p className="text-sm text-slate-500">{receipt.transactionDate?.slice(0, 10)}</p>
                            </div>
                            <p className="font-mono font-medium text-slate-900">£{(receipt.totalAmount || 0).toFixed(2)}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </ScrollArea>
            </Card>

            <Card className="bg-white border border-slate-200 shadow-sm">
              <CardHeader className="border-b border-slate-200">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Unmatched Transactions
                </CardTitle>
              </CardHeader>
              <ScrollArea className="h-[400px]">
                <CardContent className="p-0">
                  {unmatchedTransactions.length === 0 ? (
                    <div className="p-8 text-center">
                      <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                      <p className="text-slate-500">All transactions are matched!</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {unmatchedTransactions.map((txn, index) => (
                        <button
                          key={txn.id}
                          onClick={() => setSelectedTransaction(selectedTransaction?.id === txn.id ? null : txn)}
                          className={`w-full p-4 text-left transition-colors ${
                            selectedTransaction?.id === txn.id ? "bg-blue-50 border-l-4 border-l-blue-600" : "hover:bg-slate-50"
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate">{txn.description}</p>
                              <p className="text-sm text-slate-500">{txn.date}</p>
                            </div>
                            <p className={`font-mono font-medium ml-3 ${txn.amount < 0 ? "text-red-600" : "text-emerald-600"}`}>
                              {txn.amount < 0 ? "-" : "+"}£{Math.abs(txn.amount).toFixed(2)}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </ScrollArea>
            </Card>
          </div>

          {/* Match Button */}
          {selectedReceipt && selectedTransaction && (
            <div className="fixed bottom-6 right-6 z-20">
              <Card className="bg-white border border-slate-200 shadow-lg">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="text-sm">
                    <span className="text-slate-500">Match:</span>{' '}
                    <span className="font-medium">{selectedReceipt.merchantName}</span>
                    <span className="mx-2 text-slate-400">→</span>
                    <span className="font-medium">{selectedTransaction.description?.slice(0, 20)}...</span>
                  </div>
                  <Button onClick={() => handleMatch(selectedReceipt, selectedTransaction)} className="bg-slate-900 hover:bg-slate-800">
                    <Link2 className="w-4 h-4 mr-2" />
                    Match Items
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
